import { useEffect, useState } from "react";
import { Share2, Loader2, Unplug, Link2, Heart, MessageCircle, Repeat2, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const fieldCls = "rounded-xl border-brand-line focus-visible:ring-brand-red/30 focus-visible:border-brand-navy";

const num = (n) => (n == null ? "—" : new Intl.NumberFormat("en-US").format(n));
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "");

function PostRow({ p }) {
  return (
    <div className="flex items-center gap-3 border-t border-brand-line py-2 text-sm">
      <span className="flex-1 min-w-0 truncate text-brand-navy">{p.text || <span className="text-brand-ink2 italic">No caption</span>}</span>
      <span className="text-xs text-brand-ink2 shrink-0">{fmtDate(p.created_at)}</span>
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-ink2 shrink-0"><Heart className="w-3.5 h-3.5 text-brand-red" /> {num(p.likes)}</span>
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-ink2 shrink-0"><MessageCircle className="w-3.5 h-3.5" /> {num(p.comments)}</span>
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-ink2 shrink-0"><Repeat2 className="w-3.5 h-3.5" /> {num(p.shares)}</span>
    </div>
  );
}

function Channel({ title, badgeCls, followers, posts, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${badgeCls}`}>{title}</span>
        {followers != null && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-ink2"><Users className="w-3.5 h-3.5" /> {num(followers)} followers</span>
        )}
      </div>
      {posts?.length ? posts.slice(0, 5).map((p) => <PostRow key={p.id} p={p} />) : (
        <p className="text-sm text-brand-ink2 py-2">No recent posts found.</p>
      )}
    </div>
  );
}

export function SocialConnection({ conn, onChanged }) {
  const [form, setForm] = useState({ page_id: "", page_access_token: "", x_bearer_token: "", x_username: "" });
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!conn) { setStats(null); return; }
    setLoading(true);
    setStatsError(null);
    api.get("/admin/connections/social/stats")
      .then(({ data }) => setStats(data))
      .catch((err) => setStatsError(formatApiError(err.response?.data?.detail, "Could not load social stats.")))
      .finally(() => setLoading(false));
  }, [conn]);

  const connect = async () => {
    setConnecting(true);
    setConnectError(null);
    try {
      const { data } = await api.post("/admin/connections/social", {
        page_id: form.page_id.trim(),
        page_access_token: form.page_access_token.trim(),
        x_bearer_token: form.x_bearer_token.trim() || null,
        x_username: form.x_username.trim() || null,
      });
      setForm({ page_id: "", page_access_token: "", x_bearer_token: "", x_username: "" });
      toast.success(`Connected to "${data.page_name}"`);
      onChanged();
    } catch (err) {
      setConnectError(formatApiError(err.response?.data?.detail, "Could not connect social accounts."));
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    try {
      await api.delete("/admin/connections/social");
      toast.success("Social accounts disconnected");
      onChanged();
    } catch {
      toast.error("Could not disconnect.");
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-brand-line p-5 shadow-card" data-testid="social-connection">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-brand-navy"><Share2 className="w-5 h-5 text-white" strokeWidth={2.2} /></span>
          <div>
            <h2 className="font-display font-bold text-lg text-brand-navy leading-tight">Live social stats</h2>
            <p className="text-sm text-brand-ink2">{conn ? `Connected · ${conn.page_name || conn.page_id}${conn.has_x ? ` + @${conn.x_username}` : ""}` : "Facebook, Instagram & optional X engagement"}</p>
          </div>
        </div>
        {conn && (
          <button onClick={disconnect} data-testid="social-disconnect"
            className="btn-focus inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border border-brand-line text-sm font-semibold text-brand-red hover:bg-brand-red/8 transition-colors">
            <Unplug className="w-3.5 h-3.5" /> Disconnect
          </button>
        )}
      </div>

      {!conn ? (
        <div className="space-y-4" data-testid="social-connect-form">
          <ol className="list-decimal pl-5 text-sm text-brand-ink2 space-y-1">
            <li>In Meta for Developers, get a <span className="font-semibold text-brand-navy">long-lived Page access token</span> with <code className="text-xs">pages_read_engagement</code> (Graph API Explorer → /me/accounts).</li>
            <li>Copy your <span className="font-semibold text-brand-navy">Facebook Page ID</span> (Instagram Business stats come along automatically if linked to the Page).</li>
            <li>Optional: add an X API Bearer token + your @handle for tweet stats.</li>
          </ol>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-brand-navy font-semibold">Facebook Page ID</Label>
              <Input data-testid="social-pageid-input" value={form.page_id} onChange={set("page_id")} placeholder="e.g. 104857923311" className={`h-11 ${fieldCls}`} />
            </div>
            <div className="space-y-2">
              <Label className="text-brand-navy font-semibold">Page access token</Label>
              <Input data-testid="social-token-input" type="password" value={form.page_access_token} onChange={set("page_access_token")} placeholder="EAAB…" className={`h-11 ${fieldCls}`} />
            </div>
            <div className="space-y-2">
              <Label className="text-brand-navy font-semibold">X Bearer token <span className="font-normal text-brand-ink2">(optional)</span></Label>
              <Input data-testid="social-xbearer-input" type="password" value={form.x_bearer_token} onChange={set("x_bearer_token")} placeholder="AAAA…" className={`h-11 ${fieldCls}`} />
            </div>
            <div className="space-y-2">
              <Label className="text-brand-navy font-semibold">X username <span className="font-normal text-brand-ink2">(optional)</span></Label>
              <Input data-testid="social-xuser-input" value={form.x_username} onChange={set("x_username")} placeholder="@lifebutton" className={`h-11 ${fieldCls}`} />
            </div>
          </div>
          <p className="text-xs text-brand-ink2">Tokens are stored encrypted and never shown again after connecting.</p>
          {connectError && <p className="text-sm font-semibold text-brand-red" data-testid="social-connect-error">{connectError}</p>}
          <button onClick={connect} disabled={connecting || !form.page_id.trim() || !form.page_access_token.trim()} data-testid="social-connect-btn"
            className="btn-focus inline-flex items-center gap-2 h-11 px-5 rounded-full bg-brand-navy text-white font-bold hover:bg-brand-navy-700 transition-colors disabled:opacity-60">
            {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
            {connecting ? "Verifying with Meta…" : "Connect accounts"}
          </button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="social-stats">
          {loading ? (
            <div className="h-32 grid place-items-center"><Loader2 className="w-6 h-6 text-brand-red animate-spin" /></div>
          ) : statsError ? (
            <p className="text-sm font-semibold text-brand-red" data-testid="social-stats-error">{statsError}</p>
          ) : stats ? (
            <>
              {stats.facebook && (
                <Channel title="Facebook" badgeCls="bg-[#1877F2]/10 text-[#1877F2]" followers={stats.facebook.followers} posts={stats.facebook.posts} testid="social-facebook" />
              )}
              {stats.instagram && (
                <Channel title={`Instagram${stats.instagram.username ? ` · @${stats.instagram.username}` : ""}`} badgeCls="bg-pink-100 text-pink-600" followers={stats.instagram.followers} posts={stats.instagram.posts} testid="social-instagram" />
              )}
              {stats.x && (
                <Channel title={`X · @${stats.x.username}`} badgeCls="bg-brand-navy/10 text-brand-navy" followers={stats.x.followers} posts={stats.x.posts} testid="social-x" />
              )}
              {Object.entries(stats.errors || {}).map(([k, v]) => (
                <p key={k} className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid={`social-error-${k}`}>
                  <span className="capitalize">{k}</span>: {v}
                </p>
              ))}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
